// Onboarding — pixel-matched to /home/ubuntu/dropbox/clearbooks-mockup-v4.html scene 1.
// Five steps. Each step is skippable. State is autosaved on transitions.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { tenantApi, coaApi, type Tenant } from "../lib/tenant";
import { ApiError } from "../lib/api";
import { StepCompact } from "../components/StepCompact";
import { trackFunnel } from "../lib/funnel";

type StepKey = "welcome" | "business" | "industry" | "tax" | "ready";

const STEPS: Array<{ key: StepKey; pill: string; name: string; time: string }> = [
  { key: "welcome", pill: "Step 1", name: "Welcome", time: "10s" },
  { key: "business", pill: "Step 2", name: "Your business", time: "30s" },
  { key: "industry", pill: "Step 3", name: "Industry & CoA", time: "15s" },
  { key: "tax", pill: "Step 4", name: "Tax setup", time: "30s" },
  { key: "ready", pill: "Step 5", name: "Ready", time: "25s" },
];

const STEP_INDEX: Record<StepKey, number> = {
  welcome: 0, business: 1, industry: 2, tax: 3, ready: 4,
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const { data: tenant, refetch } = useQuery({
    queryKey: ["tenant"],
    queryFn: () => tenantApi.getMe(),
  });

  const [step, setStep] = useState<StepKey>("welcome");
  useEffect(() => {
    if (tenant) {
      // resume where they left off, but never auto-navigate to ready
      setStep(tenant.onboarding_step === "ready" ? "ready" : tenant.onboarding_step);
    }
  }, [tenant]);

  async function go(next: StepKey) {
    setStep(next);
    try {
      await tenantApi.setStep(next);
      trackFunnel(`onboarding_${next}`);
    } catch {
      /* non-fatal — UI continues */
    }
  }

  async function finish() {
    await tenantApi.setStep("ready").catch(() => {});
    trackFunnel("onboarding_completed");
    // Honour the marketing intent the user landed with on /pricing. If they
    // came in via "Upgrade to Pro", drop them on Settings → Billing with the
    // change-plan flow pre-selected for that plan. Otherwise → dashboard.
    let intent: { plan?: string; cycle?: string; coupon?: string } | null = null;
    try {
      const raw = localStorage.getItem("clearbooks.signupIntent");
      if (raw) intent = JSON.parse(raw);
      localStorage.removeItem("clearbooks.signupIntent");
    } catch {
      /* ignore */
    }
    if (intent?.plan) {
      const qs = new URLSearchParams();
      qs.set("upgrade", intent.plan);
      if (intent.cycle) qs.set("cycle", intent.cycle);
      if (intent.coupon) qs.set("coupon", intent.coupon);
      navigate(`/settings/billing?${qs.toString()}`, { replace: true });
      return;
    }
    navigate("/dashboard", { replace: true });
  }

  if (!tenant) {
    return (
      <div className="onboard-wrap">
        <div className="onboard-body" style={{ display: "grid", placeItems: "center", color: "var(--ink-3)" }}>
          Loading…
        </div>
      </div>
    );
  }

  const currentIdx = STEP_INDEX[step];

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">
            Onboarding<span className="new-pill">New</span>
          </div>
          <h1 className="stage-title">
            First ninety seconds. <em>Books, ready.</em>
          </h1>
          <p className="stage-sub">
            Five steps, total time under two minutes for a typical SME. Skip-friendly — anything
            not done now appears as a setup task on the dashboard later.
          </p>
        </div>
        <div className="stage-meta">
          <span>Trial · 30 days</span>
        </div>
      </div>

      <div className="onboard-wrap">
        <StepCompact steps={STEPS} current={currentIdx} onJump={(i) => go(STEPS[i].key)} />
        <div className="onboard-progress">
          {STEPS.map((s, i) => {
            const isActive = s.key === step;
            const isDone = i < currentIdx;
            return (
              <button
                key={s.key}
                className={`onboard-step ${isActive ? "active" : ""} ${isDone ? "done" : ""}`}
                onClick={() => go(s.key)}
                type="button"
              >
                <div className="step-pill">{s.pill}</div>
                <div className="step-name">{s.name}</div>
                <div className="step-time">{s.time}</div>
              </button>
            );
          })}
        </div>

        {step === "welcome" && <Welcome onNext={() => go("business")} />}
        {step === "business" && (
          <BusinessStep
            tenant={tenant}
            onBack={() => go("welcome")}
            onNext={() => go("industry")}
            onSaved={refetch}
          />
        )}
        {step === "industry" && (
          <IndustryStep
            tenant={tenant}
            onBack={() => go("business")}
            onNext={() => go("tax")}
            onSaved={refetch}
          />
        )}
        {step === "tax" && (
          <TaxStep
            tenant={tenant}
            onBack={() => go("industry")}
            onNext={() => go("ready")}
            onSaved={refetch}
          />
        )}
        {step === "ready" && (
          <ReadyStep
            tenant={tenant}
            onBack={() => go("tax")}
            onFinish={finish}
          />
        )}
      </div>
    </>
  );
}

// ─── Step 1: Welcome ───
function Welcome({ onNext }: { onNext: () => void }) {
  return (
    <div className="welcome">
      <div className="welcome-mark">C</div>
      <h2 className="welcome-title">
        Welcome to <em>ClearBooks.</em>
      </h2>
      <p className="welcome-sub">
        Built for Sri Lankan businesses. Compliance baked in. Five short steps and you're ready
        to send your first invoice.
      </p>
      <div className="welcome-times">
        <span>
          <strong>~2 min</strong> to set up
        </span>
        <span>
          <strong>0</strong> credit card needed
        </span>
        <span>
          <strong>30 days</strong> free trial
        </span>
      </div>
      <div>
        <button
          className="btn btn-primary"
          style={{ padding: "13px 32px" }}
          onClick={onNext}
        >
          Let's go →
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Business details ───
function BusinessStep({
  tenant,
  onBack,
  onNext,
  onSaved,
}: {
  tenant: Tenant;
  onBack: () => void;
  onNext: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [businessRegNo, setBusinessRegNo] = useState(tenant.business_reg_no ?? "");
  const [address, setAddress] = useState(tenant.address ?? "");
  const [phone, setPhone] = useState(tenant.phone ?? "");
  const [fyMonth, setFyMonth] = useState(String(tenant.fiscal_year_start_month));
  const [currency, setCurrency] = useState(tenant.base_currency);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(then: () => void) {
    setSaving(true);
    setError(null);
    try {
      await tenantApi.patch({
        name,
        businessRegNo: businessRegNo || null,
        address: address || null,
        phone: phone || null,
        fiscalYearStartMonth: parseInt(fyMonth, 10) || 4,
        baseCurrency: currency,
      });
      onSaved();
      then();
    } catch (err) {
      setError(err instanceof ApiError ? "Could not save. Try again." : "Something went wrong.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="onboard-body">
      <h2 className="onboard-headline">
        Tell us about <em>your business.</em>
      </h2>
      <p className="onboard-sub">
        We'll use this on your invoices, returns, and reports. You can change any of it later.
      </p>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Business name</label>
          <input
            className="form-input filled"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Business registration no.</label>
          <input
            className="form-input"
            placeholder="e.g. PV 00211846"
            value={businessRegNo}
            onChange={(e) => setBusinessRegNo(e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Address</label>
          <input
            className="form-input"
            placeholder="142 Galle Road, Colombo 04"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label className="form-label">Phone (WhatsApp)</label>
          <input
            className="form-input"
            placeholder="+94 77 234 5678"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <div className="form-help">We'll send invoices &amp; reminders from this number.</div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-label">Financial year start (month)</label>
          <input
            className="form-input"
            value={fyMonth}
            onChange={(e) => setFyMonth(e.target.value)}
          />
          <div className="form-help">Default 4 (April) for Sri Lanka.</div>
        </div>
        <div className="form-field">
          <label className="form-label">Base currency</label>
          <input
            className="form-input"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
          />
        </div>
      </div>

      {error && <div className="banner banner-error" style={{ marginTop: 8 }}>{error}</div>}

      <div className="onboard-actions">
        <a className="skip-link" onClick={onNext}>
          Skip — fill later
        </a>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onBack} disabled={saving}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={() => save(onNext)} disabled={saving}>
            {saving ? "Saving…" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Industry & CoA ───
function IndustryStep({
  tenant,
  onBack,
  onNext,
  onSaved,
}: {
  tenant: Tenant;
  onBack: () => void;
  onNext: () => void;
  onSaved: () => void;
}) {
  const [picked, setPicked] = useState<string>(tenant.industry || "services");
  const { data: templates } = useQuery({
    queryKey: ["coa-templates"],
    queryFn: () => coaApi.templates(),
  });
  const { data: detail } = useQuery({
    queryKey: ["coa-template", picked],
    queryFn: () => coaApi.template(picked),
    enabled: !!picked,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await tenantApi.setIndustry(picked);
      onSaved();
      onNext();
    } catch {
      /* swallow */
    } finally {
      setSaving(false);
    }
  }

  const pickedLabel = useMemo(
    () => templates?.templates.find((t) => t.key === picked)?.label,
    [templates, picked]
  );
  const previewAccounts = detail?.accounts.slice(0, 12) ?? [];

  return (
    <div className="onboard-body">
      <h2 className="onboard-headline">
        Pick your <em>industry.</em>
      </h2>
      <p className="onboard-sub">
        We'll set up a Sri Lankan-style chart of accounts pre-tuned for your business type.
        SLFRS-compliant. Edit anytime.
      </p>

      <div className="industry-grid">
        {templates?.templates.map((tmpl) => (
          <button
            key={tmpl.key}
            type="button"
            className={`industry-card ${picked === tmpl.key ? "selected" : ""}`}
            onClick={() => setPicked(tmpl.key)}
          >
            <div className="industry-icon">{tmpl.label.letter}</div>
            <div className="industry-name">{tmpl.label.name}</div>
            <div className="industry-sub">{tmpl.label.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        <div>
          <div className="form-label" style={{ marginBottom: 10 }}>
            Pre-loaded chart of accounts · {pickedLabel?.name ?? "—"} template
          </div>
          <div className="coa-preview">
            {previewAccounts.map((a) => (
              <div className="coa-line" key={a.code}>
                <span className="coa-num">{a.code}</span>
                <span className={`coa-name ${a.depth === 0 ? "bold" : ""}`}>
                  {"  ".repeat(a.depth)}
                  {a.name}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="form-label" style={{ marginBottom: 10 }}>
            What's pre-configured for {pickedLabel?.name ?? "—"}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--ink-2)" }}>
            · {detail?.accounts.length ?? 0} standard accounts
            <br />· VAT, SSCL, WHT, EPF/ETF/APIT payable accounts pre-wired
            <br />· SLFRS-aligned grouping (Assets / Liabilities / Equity / Revenue / Expense)
            <br />· System accounts cannot be deleted (e.g. tax payables)
            <br />· You can add, rename, or hide any account in Settings → Chart of accounts
          </div>
        </div>
      </div>

      <div className="onboard-actions">
        <a className="skip-link" onClick={onNext}>
          Skip — choose later
        </a>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onBack} disabled={saving}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Loading template…" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Tax setup ───
function TaxStep({
  tenant,
  onBack,
  onNext,
  onSaved,
}: {
  tenant: Tenant;
  onBack: () => void;
  onNext: () => void;
  onSaved: () => void;
}) {
  const [vat, setVat] = useState(tenant.vat_registered);
  const [vatNo, setVatNo] = useState(tenant.vat_registration_no ?? "");
  const [filingBasis, setFilingBasis] = useState<"accrual" | "cash">(
    (tenant.vat_filing_basis as "accrual" | "cash") || "accrual"
  );
  const [sscl, setSscl] = useState(tenant.sscl_liable);
  const [payroll, setPayroll] = useState(tenant.payroll_active);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await tenantApi.patch({
        vatRegistered: vat,
        vatRegistrationNo: vat ? vatNo || null : null,
        vatFilingBasis: filingBasis,
        ssclLiable: sscl,
        payrollActive: payroll,
      });
      onSaved();
      onNext();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="onboard-body">
      <h2 className="onboard-headline">
        Set up <em>tax.</em>
      </h2>
      <p className="onboard-sub">
        Toggle what applies to your business. We'll wire VAT, SSCL, and WHT into every invoice
        and bill automatically.
      </p>

      <div className={`toggle-card ${vat ? "on" : ""}`} onClick={() => setVat((v) => !v)}>
        <div>
          <div className="toggle-card-name">Business is VAT-registered</div>
          <div className="toggle-card-sub">
            Standard 18%, reduced 8%, monthly returns. We'll handle Taxable, Zero-rated and
            Exempt categories.
          </div>
        </div>
        <div className={`toggle ${vat ? "on" : ""}`} />
      </div>

      {vat && (
        <div className="form-row" style={{ marginTop: 6, marginBottom: 16 }}>
          <div className="form-field">
            <label className="form-label">VAT registration number</label>
            <input
              className="form-input num"
              placeholder="114 587 234 7000"
              value={vatNo}
              onChange={(e) => setVatNo(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Filing basis</label>
            <select
              className="form-input"
              value={filingBasis}
              onChange={(e) => setFilingBasis(e.target.value as "accrual" | "cash")}
            >
              <option value="accrual">Accrual (recommended)</option>
              <option value="cash">Cash (IRD-approved only)</option>
            </select>
            <div className="form-help">Cash basis available with IRD approval.</div>
          </div>
        </div>
      )}

      <div className={`toggle-card ${sscl ? "on" : ""}`} onClick={() => setSscl((v) => !v)}>
        <div>
          <div className="toggle-card-name">Liable for SSCL</div>
          <div className="toggle-card-sub">
            Social Security Contribution Levy at 2.5% on services and applicable goods.
          </div>
        </div>
        <div className={`toggle ${sscl ? "on" : ""}`} />
      </div>

      <div className={`toggle-card ${payroll ? "on" : ""}`} onClick={() => setPayroll((v) => !v)}>
        <div>
          <div className="toggle-card-name">Run payroll &amp; file EPF/ETF/APIT</div>
          <div className="toggle-card-sub">
            If you have employees, we'll handle deductions, payslips, and IRD/EPF filings.
          </div>
        </div>
        <div className={`toggle ${payroll ? "on" : ""}`} />
      </div>

      <div className="onboard-actions">
        <a className="skip-link" onClick={onNext}>
          Skip — set up later
        </a>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onBack} disabled={saving}>
            ← Back
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Ready ───
function ReadyStep({
  tenant,
  onBack,
  onFinish,
}: {
  tenant: Tenant;
  onBack: () => void;
  onFinish: () => void;
}) {
  const { data: coa } = useQuery({
    queryKey: ["coa-mine"],
    queryFn: () => coaApi.mine(),
  });

  const taxesCount = [tenant.vat_registered, tenant.sscl_liable, tenant.payroll_active].filter(
    Boolean
  ).length;

  return (
    <div className="onboard-body" style={{ textAlign: "center" }}>
      <div
        style={{
          fontSize: 44,
          fontFamily: "'Fraunces', serif",
          fontWeight: 500,
          lineHeight: 1,
          color: "var(--accent)",
          marginBottom: 8,
        }}
      >
        ✓
      </div>
      <h2 className="onboard-headline">
        Books are <em>ready.</em>
      </h2>
      <p className="onboard-sub" style={{ marginLeft: "auto", marginRight: "auto" }}>
        You can send your first invoice now. We'll keep nudging you to complete a few more
        things this week.
      </p>

      <div className="ready-grid" style={{ marginTop: 20 }}>
        <div className="ready-tile">
          <div className="ready-tile-num">{coa?.accounts.length ?? "—"}</div>
          <div className="ready-tile-name">CoA accounts loaded</div>
          <div className="ready-tile-sub">
            {tenant.industry.charAt(0).toUpperCase() + tenant.industry.slice(1)} template · SLFRS
          </div>
        </div>
        <div className="ready-tile">
          <div className="ready-tile-num">{taxesCount}</div>
          <div className="ready-tile-name">Taxes wired up</div>
          <div className="ready-tile-sub">
            {[
              tenant.vat_registered ? "VAT" : null,
              tenant.sscl_liable ? "SSCL" : null,
              tenant.payroll_active ? "EPF/ETF/APIT" : null,
            ]
              .filter(Boolean)
              .join(" · ") || "None yet"}
          </div>
        </div>
        <div className="ready-tile">
          <div className="ready-tile-num">3</div>
          <div className="ready-tile-name">Setup tasks queued</div>
          <div className="ready-tile-sub">Bank · Accountant · Branding</div>
        </div>
      </div>

      <div className="ready-tasks">
        <h4>Three things you can do this week</h4>
        <div className="ready-task">
          <div className="check" />
          <span>Connect your first bank account · upload a statement PDF</span>
        </div>
        <div className="ready-task">
          <div className="check" />
          <span>Invite your accountant or bookkeeper</span>
        </div>
        <div className="ready-task">
          <div className="check" />
          <span>Add your logo &amp; customise invoice template</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        <button className="btn btn-line" onClick={onBack}>
          ← Back
        </button>
        <button className="btn btn-primary" onClick={onFinish}>
          Send your first invoice →
        </button>
      </div>
    </div>
  );
}
